import mixpanel, { Dict } from 'mixpanel-browser';
import { environment } from '@/environment';
import { IUser, IMixpanelExperimentsCookie } from '@/models';
import { isUserAnonymous } from '@/utils';
import { logger } from '@/utils';
import { runOnce } from '@/utils';
import { ANALYTICS, MIXPANEL_CONFIG } from '@/constants/analytics/analytics';
import { cookiesUtils } from '@/utils/storage/cookies';
import { COOKIE_KEYS } from '@/constants/storage/cookies';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

class MixpanelService {
  private isInitialized = false;

  /**
   * Initialize Mixpanel with the project token
   * If Mixpanel API is blocked, uses proxy through user-state service
   */
  public async init(user: IUser): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    const mixpanelToken = environment.mixpanelToken;

    if (!mixpanelToken) {
      logger.warn(
        'Mixpanel token is not provided. Analytics will not be tracked.',
        {
          tags: {
            feature: LOGGER_FEATURE.MIXPANEL,
            action: LOGGER_ACTION.INIT,
          },
        }
      );
      return;
    }

    const isMixpanelReachable = await this.testMixpanelReachability();
    const proxyApiHost = `${environment.userStateApiBaseUrl}${MIXPANEL_CONFIG.PROXY_PATH}`;

    mixpanel.init(mixpanelToken, {
      track_pageview: !environment.isDevelopment,
      persistence: 'localStorage',
      debug: environment.isDevelopment,
      ignore_dnt: true,
      ...(isMixpanelReachable ? {} : { api_host: proxyApiHost }),
    });

    this.isInitialized = true;

    // Sync identity across extension and websites via cookie
    this.identifyUser(user);

    // Set initial user profile (for all users - we've identified them)
    this.setUserProfileOnInit(user);
  }

  /**
   * Test if Mixpanel API is reachable (not blocked by browser/extension)
   * @returns true if Mixpanel is reachable, false otherwise
   */
  private async testMixpanelReachability(): Promise<boolean> {
    try {
      const response = await fetch(MIXPANEL_CONFIG.TRACK_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        body: '',
      });

      return response.ok || response.type === 'opaque';
    } catch (error) {
      return false;
    }
  }

  /**
   * Identify user and sync identity across extension and websites via cookie
   * Only calls identify() if the identity actually changed
   * @param user - The user object
   */
  private identifyUser(user: IUser): void {
    if (!this.isInitialized) {
      logger.warn(
        'Mixpanel is not initialized. Call init() first before identifying users.',
        {
          tags: {
            feature: LOGGER_FEATURE.MIXPANEL,
            action: LOGGER_ACTION.INIT,
          },
        }
      );
      return;
    }

    const currentIdentity = this.get_distinct_id();
    const isAnonymous = isUserAnonymous(user);
    const storedIdentity = cookiesUtils.getCookie(COOKIE_KEYS.MP_IDENTITY);

    // Determine the identity to use
    let newIdentity: string | undefined;
    if (!isAnonymous && currentIdentity !== user.uid) {
      // Logged-in user with different identity → use Firebase uid
      newIdentity = user.uid;
    } else if (
      isAnonymous &&
      storedIdentity &&
      currentIdentity !== storedIdentity
    ) {
      // Anonymous user with existing cookie → sync to cookie value
      newIdentity = storedIdentity;
    }

    const identityToUse = newIdentity ?? currentIdentity;
    const shouldUpdate = !!newIdentity || !storedIdentity;

    if (shouldUpdate) {
      cookiesUtils.setCookie(COOKIE_KEYS.MP_IDENTITY, identityToUse);
      mixpanel.identify(identityToUse);
    }
  }

  /**
   * Set user profile properties on init (for all users - anonymous and logged-in)
   * @param user - The user object
   */
  private setUserProfileOnInit(user: IUser): void {
    const isAnonymous = isUserAnonymous(user);
    this.setUserProfile({
      [ANALYTICS.PROFILE_PROPERTIES.IS_LOGGED_IN]: !isAnonymous,
      [ANALYTICS.PROFILE_PROPERTIES.HAD_TRIAL]: false,
      [ANALYTICS.PROFILE_PROPERTIES.FIREBASE_ID]: user.uid,
      [ANALYTICS.PROFILE_PROPERTIES.UUID]: user.uid,
    });
  }

  /**
   * Set user profile properties in Mixpanel People
   * @param properties - Properties to set on the user profile
   */
  public setUserProfile(properties: Record<string, unknown>): void {
    if (!this.isInitialized) {
      return;
    }
    mixpanel.people.set(properties);
  }

  /**
   * Set user profile properties after login
   * @param email - User's email
   * @param uid - User's Firebase ID
   */
  public setUserProfileOnLogin(email: string, uid: string): void {
    this.setUserProfile({
      [ANALYTICS.PROFILE_PROPERTIES.EMAIL]: email,
      [ANALYTICS.PROFILE_PROPERTIES.IS_LOGGED_IN]: true,
      [ANALYTICS.PROFILE_PROPERTIES.FIREBASE_ID]: uid,
      [ANALYTICS.PROFILE_PROPERTIES.UUID]: uid,
    });
  }

  /**
   * Set user tier (premium status) on profile
   * @param isPremium - Whether the user is premium
   */
  public setUserTier(isPremium: boolean): void {
    const tierValue = isPremium
      ? ANALYTICS.VALUES.PREMIUM
      : ANALYTICS.VALUES.FREE;
    this.setUserProfile({
      [ANALYTICS.PROFILE_PROPERTIES.TIER]: tierValue,
    });
  }

  /**
   * Update user profile properties on logout
   * Note: We don't call reset() to keep the same device_id, so multiple users
   * on the same device will be linked in the same identity cluster in Mixpanel
   * @see https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users-simplified#multiple-users-one-device
   */
  public setUserProfileOnLogout(): void {
    this.setUserProfile({
      [ANALYTICS.PROFILE_PROPERTIES.IS_LOGGED_IN]: false,
      [ANALYTICS.PROFILE_PROPERTIES.TIER]: ANALYTICS.VALUES.FREE,
    });
  }

  /**
   * Track an event in Mixpanel
   * @param eventName - Name of the event to track
   * @param payload - Optional event properties
   */
  public track(eventName: string, payload?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Mixpanel not initialized, event not tracked', {
        tags: { feature: LOGGER_FEATURE.MIXPANEL, action: LOGGER_ACTION.TRACK },
        extra: { eventName },
      });
      return;
    }

    mixpanel.track(eventName, payload, { send_immediately: true });
  }

  /**
   * Identify a user in Mixpanel
   * @param userId - The unique user identifier
   * @param userProperties - Optional properties about the user
   */
  public identify(userId: string, userProperties?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn(
        'Mixpanel is not initialized. Call init() first before identifying users.',
        {
          tags: {
            feature: LOGGER_FEATURE.MIXPANEL,
            action: LOGGER_ACTION.INIT,
          },
        }
      );
      return;
    }

    mixpanel.identify(userId);

    if (userProperties) {
      mixpanel.people.set(userProperties);
    }
  }

  public fireExperimentStart(key: string, val: any, variationId: number): void {
    runOnce(key, () => {
      this.track(ANALYTICS.EVENTS.EXPERIMENT_STARTED, {
        [ANALYTICS.PROPERTIES.EXPERIMENT_NAME]: key,
        [ANALYTICS.PROPERTIES.VARIANT_NAME]: variationId,
        [ANALYTICS.PROPERTIES.VARIANT_VALUE]: val,
      });
    });
  }

  /**
   * Fires a single experiment event from the extension cookie and removes its entry.
   * The extension stores pending experiments in a cookie because it cannot use mixpanel-js from a service worker.
   */
  public fireSpecificExperimentFromCookie(experimentKey: string): void {
    if (!this.isInitialized) {
      logger.warn('Mixpanel not initialized, cannot fire experiment', {
        tags: {
          feature: LOGGER_FEATURE.MIXPANEL,
          action: LOGGER_ACTION.TRACK,
        },
        extra: { experimentKey },
      });
      return;
    }

    try {
      const cookieValue = cookiesUtils.getCookie(COOKIE_KEYS.MP_EXPERIMENTS);
      if (!cookieValue) return;

      const experiments: IMixpanelExperimentsCookie = JSON.parse(cookieValue);
      if (
        typeof experiments !== 'object' ||
        experiments === null ||
        Array.isArray(experiments)
      ) {
        return;
      }

      const experiment = experiments[experimentKey];
      if (!experiment?.data || experiment.sent) return;

      this.fireExperimentStart(
        experimentKey,
        experiment.data.value,
        experiment.data.variationId
      );

      const { [experimentKey]: _, ...remaining } = experiments;
      if (Object.keys(remaining).length === 0) {
        cookiesUtils.removeCookie(COOKIE_KEYS.MP_EXPERIMENTS);
      } else {
        cookiesUtils.setRawCookie(
          COOKIE_KEYS.MP_EXPERIMENTS,
          JSON.stringify(remaining)
        );
      }
    } catch (error) {
      logger.error('Failed to fire specific experiment from cookie', error, {
        tags: {
          feature: LOGGER_FEATURE.MIXPANEL,
          action: LOGGER_ACTION.TRACK,
        },
      });
    }
  }

  /**
   * Reset the current user
   */
  public reset(): void {
    if (this.isInitialized) {
      mixpanel.reset();
    }
  }

  public register(attributes: Dict): void {
    if (this.isInitialized) {
      mixpanel.register(attributes);
    }
  }

  public unregister(key: string): void {
    if (this.isInitialized) {
      mixpanel.unregister(key);
    }
  }

  public get_distinct_id(): string {
    if (!this.isInitialized) {
      return '';
    }
    return mixpanel.get_distinct_id();
  }
}

export const mixpanelUtils = new MixpanelService();
